<template>
  <div>
    <breadcrumb :items="breadcrumbs"></breadcrumb>
    <v-container fluid v-if="items.length > 0">
      <v-row class="pb-6">
        <v-col cols="6">
          <h3>{{ $t("farm.farms") }}</h3>
        </v-col>
        <v-col cols="6">
          <v-btn
            class="float-right"
            color="primary"
            @click="goToFarmRegistration"
            >{{ $t("farm.registerAfarm") }}</v-btn
          >
        </v-col>
      </v-row>
      <v-card elevation="0">
        <v-card-text>
          <v-data-table
            :headers="tableHeaders"
            :items="items"
            v-model="selected"
            :items-per-page="10"
            :loading="listLoading"
            loading-text="Loading Farm... Please wait"
            hide-default-footer
            :header-props="{
              'sort-icon': 'mdi-unfold-more-horizontal',
            }"
            @pagination="paginationChanged"
          >
            <template v-slot:top>
              <div class="d-flex mt-5">
                <v-text-field
                  prepend-inner-icon="mdi-magnify"
                  outlined
                  height="5px"
                  :placeholder="$t('search')"
                  dense
                  v-model="search"
                  class="shrink"
                ></v-text-field>

                <v-spacer></v-spacer>
                <div class="py-3">
                  {{ tableOptions.page }} of
                  {{ totalPages }}
                </div>
                <v-btn
                  class="mx-2"
                  fab
                  small
                  outlined
                  color="primary"
                  :disabled="tableOptions.page <= 1"
                  @click="pageChange(false)"
                >
                  <v-icon dark> mdi-chevron-left </v-icon>
                </v-btn>
                <v-btn
                  class="mx-2"
                  fab
                  small
                  outlined
                  color="primary"
                  :disabled="tableOptions.page >= totalPages"
                  @click="pageChange(true)"
                >
                  <v-icon dark> mdi-chevron-right </v-icon>
                </v-btn>
                <v-menu
                  v-model="menu"
                  :close-on-content-click="false"
                  :nudge-width="200"
                  left
                  :min-width="417"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      class="mx-2"
                      fab
                      small
                      outlined
                      color="primary"
                      v-bind="attrs"
                      v-on="on"
                    >
                      <v-icon dark> mdi-cog-outline </v-icon>
                    </v-btn>
                  </template>

                  <v-card width="417">
                    <v-list>
                      <v-list-item>
                        <v-list-item-content>
                          <v-list-item-title>{{
                            $t("farm.customization")
                          }}</v-list-item-title>
                        </v-list-item-content>
                      </v-list-item>
                      <v-divider></v-divider>
                      <div class="pl-3 pt-2 font-weight-normal">
                        {{ $t("farm.recordsPerpage") }}
                      </div>
                      <v-list-item>
                        <v-list-item-content>
                          <v-slider
                            v-model="itemsPerPageSlider"
                            :tick-labels="itemsPerPageOptions"
                            :max="3"
                            step="1"
                            ticks="always"
                            tick-size="4"
                          ></v-slider>
                        </v-list-item-content>
                      </v-list-item>
                      <v-divider class="mt-n3"></v-divider>
                    </v-list>
                    <v-card-text>
                      <v-container fluid>
                        <v-row>
                          <v-col
                            class="mt-n9"
                            cols="6"
                            v-for="header in headers"
                            :key="header.value"
                          >
                            <v-checkbox
                              v-model="selectedHeaders"
                              :label="header.text"
                              color="primary"
                              :value="header.text"
                              hide-details
                              :disabled="
                                header.text == 'ID' ||
                                header.text == 'Tag' ||
                                header.text == '' ||
                                header.text == 'Ration/Name ID' ||
                                header.text == 'Status' ||
                                header.text == 'Action'
                              "
                            ></v-checkbox>
                          </v-col>
                        </v-row>
                      </v-container>
                    </v-card-text>
                    <v-layout justify-center>
                      <v-card-actions class="mb-5">
                        <v-spacer></v-spacer>

                        <v-btn
                          outlined
                          color="primary"
                          width="190"
                          @click="resetDefault"
                        >
                          {{ $t("farm.restoreDefault") }}
                        </v-btn>
                        <v-btn color="primary" @click="resetTableStructure">
                          {{ $t("farm.apply") }}
                        </v-btn>
                      </v-card-actions>
                    </v-layout>
                  </v-card>
                </v-menu>
              </div>
            </template>
            <template v-slot:progress>
              <v-progress-linear
                color="primary"
                :height="8"
                indeterminate
              ></v-progress-linear>
            </template>
            <template v-slot:no-data>{{ $t("noDataAvailable") }}</template>
            <template v-if="items.length > 0" v-slot:body="{ items }">
              <tbody>
                <tr
                  class="my-3"
                  v-for="item in items"
                  :key="item.id"
                  style="position: relative; width: 0; height: 0"
                  @click="rowClicked(item)"
                >
                  <template>
                    <td
                      v-if="
                        tableHeaders.filter(el => el.text == `Farm Id`).length >
                        0
                      "
                    >
                      {{ item.id }}
                    </td>
                    <td
                      v-if="
                        tableHeaders.filter(
                          el => el.text == `Gov Registration Number #`
                        ).length > 0
                      "
                    >
                      {{ item.govRegistrationNum }}
                    </td>
                    <td
                      v-if="
                        tableHeaders.filter(el => el.text == `Cooperative ID`)
                          .length > 0
                      "
                    >
                      {{ item.cooperativeId }}
                    </td>
                    <td
                      v-if="
                        tableHeaders.filter(el => el.text == `Farm Owner`)
                          .length > 0
                      "
                    >
                      {{
                        `${
                          (item.farmOwnerAssoc &&
                            item.farmOwnerAssoc.firstName) ||
                          "N"
                        } ${
                          (item.farmOwnerAssoc &&
                            item.farmOwnerAssoc.lastName) ||
                          "/A"
                        }`
                      }}
                    </td>
                    <td
                      v-if="
                        tableHeaders.filter(el => el.text == `Farm Type`)
                          .length > 0
                      "
                    >
                      {{ item?.farmTypeAssoc?.name }}
                    </td>
                    <!-- <td>
                      {{ item.contractMating }}
                    </td> -->
                    <td>
                      <div class="float-right">
                        <v-btn icon
                          ><v-icon color="blue" @click.stop="editFarm(item.id)"
                            >mdi-pencil</v-icon
                          ></v-btn
                        >
                        <v-btn icon
                          ><v-icon color="red" @click.stop="destroyFarm(item)"
                            >mdi-trash-can</v-icon
                          ></v-btn
                        >
                      </div>
                    </td>
                  </template>
                </tr>
              </tbody>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>
    </v-container>
    <v-progress-linear indeterminate color="green" v-else></v-progress-linear>
    <confirm-box
      v-if="deleteFarm"
      :dialog="deleteFarm"
      title="Warning"
      message="Are you sure you want to delete this record?"
      confirmBtnText="Delete"
      :confirmData="itemToDelete"
      @confirm:action="deleteFarmConfirmed"
    />
  </div>
</template>
<script>
import LoadingMixin from "@/mixins/LoadingMixin";
import { getFarmList, deleteFarm } from "@/graphql/Farm.gql";
import ConfirmBox from "@/components/ConfirmBox.vue";
import FarmService from "@/_services/farmService";

export default {
  apollo: {
    getFarmList: {
      query: getFarmList,
      debounce: 1000,
      update: data => data.getFarmList,
      fetchPolicy: "no-cache",
      variables() {
        return {
          farmListInput: {
            page: this.tableOptions.page,
            limit: this.tableOptions.limit,
            searchPhrase: this.search,
          },
        };
      },
      async result({ data }) {
        this.listLoading = false;
        const role = this.$store.getters.getUser?.userDetail?.role;
        if (role == "farmer") {
          // this.$router.replace({name:'My farm',params:{farmId:data.id}})
          const { data } = await FarmService.getPrimaryFarm();
          this.$router.replace({
            path: `/myfarm/detail/${(data && data.id) || 1}`,
          });
          this.$router.push({
            path: `/myfarm/detail/${(data && data.id) || 1}`,
          });
        } else {
          this.items = data.getFarmList?.rows;
          this.totalPages = data?.getFarmList?.totalPages;
          this.totalRecord = data?.getFarmList?.totalRecord;
          console.log("AVAILABLE QUICK FILTER", data);
        }
      },
      error(errors) {
        this.listLoading = false;
        console.log("errors", errors);
      },
    },
  },
  data() {
    return {
      listLoading: false,
      totalRecord: 0,
      menu: false,
      itemsPerPageSlider: 0,
      itemsPerPageOptions: [5, 10, 25, 50],
      totalPages: 0,
      search: "",
      selected: [],
      loadingText: this.$t("loadingPleaseWait"),
      productionSystem: [],
      headers: [
        {
          text: this.$t("farm.farmID"),
          align: "start",
          value: "id",
          class: "black--text",
        },
        {
          text: this.$t("govtRegistry"),
          value: "govRegistrationNum",
          class: "black--text",
        },
        {
          text: this.$t("cooperativeId"),
          value: "cooperativeId",
          class: "black--text",
        },
        {
          text: this.$t("farm.farmOwner"),
          value: "ownerId",
          class: "black--text",
        },
        {
          text: this.$t("farm.farmType"),
          value: "farmType",
          class: "black--text",
        },
        // {
        //   text: this.$t("farm.contractMating"),
        //   value: "contractMating",
        //   class: "black--text",
        // },
        {
          text: this.$t("action"),
          value: "action",
          align: "left",
          sortable: false,
          class: "black--text",
        },
      ],
      selectedHeaders: [],
      allSelectedHeaders: [
        "Farm Id",
        "Gov Registration Number #",
        "Cooperative ID",
        "Farm Owner",
        "Farm Type",
        "Action",
        // "Contract Mating",
      ],
      items: [],
      tableHeaders: [],

      breadcrumbs: [
        {
          text: this.$t("dashboard"),
          disabled: false,
          to: "/",
        },
        {
          text: this.$t("farms"),
          disabled: true,
        },
      ],
      tableOptions: { page: 1, limit: 10 },
      itemToDelete: null,
      deleteFarm: false,
    };
  },
  watch: {
    search() {
      this.listLoading = true;
    },
  },
  async mounted() {
    this.startLoading();
    this.listLoading = true;
    const { data } = await FarmService.getPrimaryFarm();
    const role = await this.$store.getters.getUser?.userDetail?.role;
    console.log("roles", role, data);
    console.log("userObj in farmlist", this.$store.getters.getUser);
    if (role == "farmer") {
      // this.$router.replace({name:'My farm',params:{farmId:data.id}})
      this.$router.replace({
        path: `/myfarm/detail/${(data && data.id) || 1}`,
      });
      this.$router.replace({
        path: `/myfarm/detail/${(data && data.id) || 1}`,
      });
      this.$router.replace({
        path: `/myfarm/detail/${(data && data.id) || 1}`,
      });
      this.$router.replace({
        path: `/myfarm/detail/${(data && data.id) || 1}`,
      });
    }
    this.tableHeaders = this.headers;
    this.selectedHeaders = this.allSelectedHeaders;
    this.listLoading = false;
    this.stopLoading();
  },
  components: {
    "confirm-box": ConfirmBox,
  },
  methods: {
    rowClicked(item) {
      console.log(item);
      this.$router.push({ name: "My farm", params: { farmId: item.id } });
    },
    destroyFarm(item) {
      // event.preventDefault();
      this.itemToDelete = item;
      this.deleteFarm = true;
    },
    deleteFarmConfirmed(action, item) {
      if (action) {
        this.startLoading();
        this.$apollo
          .mutate({
            mutation: deleteFarm,
            variables: {
              farmId: parseInt(item.id),
            },
          })
          .then(async ({ data }) => {
            this.stopLoading();
            console.log(data);
            if (data?.deleteFarm) {
              this.$notify({
                title: this.$t("success"),
                text: this.$t("farmDeletedSuccessfully"),
                type: "success",
              });
              await this.$apollo.queries.getFarmList.refetch();
            }
          })
          .catch(e => {
            this.stopLoading();
            console.log("arc", e);
          });
      }
      this.itemToDelete = null;
      this.deleteFarm = false;
    },
    async editFarm(farmId) {
      this.$router.push({
        name: "EditFarms",
        params: { farmId: farmId },
      });
    },
    goToFarmRegistration() {
      this.$router.push({ name: "FarmRegistartion" });
    },
    createGroupName() {
      this.unGroupedAnimals = false;
      this.addGroupName = true;
    },
    pageChange(t) {
      this.listLoading = true;
      if (t) this.tableOptions.page = Number(this.tableOptions.page) + 1;
      else this.tableOptions.page = Number(this.tableOptions.page) - 1;

      this.$router.replace({ query: { page: this.tableOptions.page } });
    },
    paginationChanged() {
      this.$router.replace({ query: { page: this.tableOptions.page } });
    },

    resetDefault() {
      this.tableHeaders = this.headers;
      this.selectedHeaders = this.allSelectedHeaders;
      this.menu = false;
    },
    resetTableStructure() {
      this.tableHeaders = this.headers.filter(header =>
        this.selectedHeaders.includes(header.text)
      );
      this.tableOptions.limit =
        this.itemsPerPageOptions[this.itemsPerPageSlider];
      this.menu = false;
      // this.stopLoading();
    },
    searchFarm() {
      if (this.search == "") return;
      if (this.checkBreakPoints) {
        this.tabletTableOptions.page = 1;
        // this.getTabletAnimalsData();
      } else {
        this.tableOptions.page = 1;
        // this.getAnimalsData();
      }
      this.loading = true;
      this.$apollo.queries.getFarmList.refetch();
    },
  },
  mixins: [LoadingMixin],
};
</script>

<style lang="scss" scoped>
.border-active {
  border: 1px solid green !important;
}

.border-deactivated {
  border: 1px solid red !important;
}
:deep.subHeader .v-data-table-header {
  background-color: #fde9c7 !important;
}
</style>
